import "./style.scss";

import CheckIcon from "@mui/icons-material/Check";
import { buildManagementPreviewDisplayFromSlice } from "components/AdvanceManagement/utils/managementPreviewDisplay";
import ModalBox from "components/Modal";
import type { BasicTableColumn } from "components/shared/BasicTable";
import TermManagementPreviewSection from "components/shared/TermManagementPreviewSection";
import AdvanceSlicesRecap from "components/TermRecap/AdvanceSlicesRecap";
import { Body, SmallBody } from "helpers/TypographyComponents";
import { formatNumber } from "helpers/utils";
import { type CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import type { TPeriod } from "store/periodStore";
import { useAppDispatch } from "store/store";
import { getTermPeriodDataById, type TermPeriodDataImpactSummary } from "store/termStore";

export type ManagementImpactKind = "balanceN1" | "grossDuty";

type PaymentSlicePreviewRow = {
    amount: number;
    paymentDate: Date | null;
    status: 0 | 1 | 2;
    comment: string | null;
    periodLinks: ManagementImpactKind[];
};

const SECTION_TITLE_STYLE: CSSProperties = { fontWeight: 500 };

interface IManagementPreviewModal {
    open: boolean;
    onClose: () => void;
    slice: IAdvanceSlice | null;
    impactKind: ManagementImpactKind | null;
    periods: TPeriod[];
    advanceSlices: IAdvanceSlice[];
}

function periodForId(periods: TPeriod[], periodId: number | null | undefined): TPeriod | undefined {
    if (!periodId) return undefined;
    return periods.find((p) => p.id === periodId);
}

function isSliceLinkedToModal(
    modalPeriodId: number | null | undefined,
    modalTpdId: number | null,
    slicePeriodId: number | null | undefined,
    sliceTpdId: number | null | undefined,
): boolean {
    const matchesPeriod = modalPeriodId != null && slicePeriodId === modalPeriodId;
    const matchesTpd = modalTpdId != null && sliceTpdId === modalTpdId;
    return matchesPeriod || matchesTpd;
}

/** How the slice is linked to the modal period (opening balance and/or gross duty). */
function getSlicePeriodLinks(
    sliceRow: IAdvanceSlice,
    modalPeriodId: number | null | undefined,
    modalTpdId: number | null,
): ManagementImpactKind[] {
    if (modalPeriodId == null && modalTpdId == null) return [];

    const links: ManagementImpactKind[] = [];

    if (
        isSliceLinkedToModal(
            modalPeriodId,
            modalTpdId,
            sliceRow.legacyImpactBalanceN1TpdPeriodId,
            sliceRow.legacyImpactBalanceN1TpdId,
        )
    ) {
        links.push("balanceN1");
    }

    if (
        isSliceLinkedToModal(modalPeriodId, modalTpdId, sliceRow.impactGrossDutyPeriodId, sliceRow.impactGrossDutyTpdId)
    ) {
        links.push("grossDuty");
    }

    return links;
}

const SLICE_PERIOD_LINK_LABEL_KEYS: Record<ManagementImpactKind, string> = {
    balanceN1: "AdvancePayment.ManagementPreviewSliceLinkOpeningBalance",
    grossDuty: "AdvancePayment.ManagementPreviewSliceLinkAdvanceGrossDuty",
};

const MODAL_STYLE: CSSProperties = {
    width: "min(960px, 96vw)",
    minWidth: "min(860px, 96vw)",
    maxWidth: "96vw",
};

const ManagementPreviewModal = ({
    open,
    onClose,
    slice,
    impactKind,
    periods,
    advanceSlices,
}: IManagementPreviewModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [status, setStatus] = useState<"idle" | "pending" | "failed">("idle");
    const [tpd, setTpd] = useState<TermPeriodDataImpactSummary | null>(null);

    const tpdId = useMemo(() => {
        if (!slice || !impactKind) return null;
        return impactKind === "balanceN1" ? slice.legacyImpactBalanceN1TpdId : slice.impactGrossDutyTpdId;
    }, [slice, impactKind]);

    const periodId =
        impactKind === "balanceN1" ? slice?.legacyImpactBalanceN1TpdPeriodId : slice?.impactGrossDutyPeriodId;

    const period = useMemo(() => {
        const fromSlice = periodForId(periods, periodId);
        if (fromSlice) return fromSlice;
        return periodForId(periods, tpd?.periodId);
    }, [periods, periodId, tpd?.periodId]);

    useEffect(() => {
        if (!open || tpdId == null) {
            setTpd(null);
            setStatus("idle");
            return;
        }

        setStatus("pending");
        setTpd(null);
        void dispatch(getTermPeriodDataById({ tpdId })).then((result) => {
            if (result.meta.requestStatus === "fulfilled") {
                setTpd(result.payload as TermPeriodDataImpactSummary);
                setStatus("idle");
            } else {
                setStatus("failed");
            }
        });
    }, [open, tpdId, dispatch]);

    const impactTargetLabel =
        impactKind === "balanceN1" ? t("AdvancePayment.N1Balance") : t("AdvancePayment.GrossDuty");

    const managementPreviewDisplay = useMemo(
        () => (slice ? buildManagementPreviewDisplayFromSlice(slice) : null),
        [slice],
    );

    const paymentSliceRows = useMemo((): PaymentSlicePreviewRow[] => {
        if (!slice || !impactKind) return [];
        const related = advanceSlices.filter((s) => s.paymentId === slice.paymentId);
        const rows = (related.length > 0 ? related : [slice]).sort((a, b) => {
            const dateA = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
            const dateB = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
            return dateA - dateB || a.id - b.id;
        });
        return rows.map((s) => ({
            amount: s.amount,
            paymentDate: s.paymentDate,
            status: s.status as 0 | 1 | 2,
            comment: s.comment ?? null,
            periodLinks: getSlicePeriodLinks(s, periodId, tpdId ?? null),
        }));
    }, [slice, advanceSlices, impactKind, tpdId, periodId]);

    const paymentSlicesTotal = useMemo(
        () => paymentSliceRows?.reduce((sum, s) => sum + Number(s.amount), 0) / 100,
        [paymentSliceRows],
    );

    const hasImpactedSlices = paymentSliceRows.some((s) => s.periodLinks.length > 0);

    const impactedSlicesTotal = useMemo(
        () =>
            paymentSliceRows.filter((s) => s.periodLinks.length > 0).reduce((sum, s) => sum + Number(s.amount), 0) /
            100,
        [paymentSliceRows],
    );
    const hasNonImpactedSlices = paymentSliceRows.some((s) => s.periodLinks.length === 0);

    const sliceImpactTrailingColumns = useMemo((): BasicTableColumn<PaymentSlicePreviewRow>[] => {
        if (paymentSliceRows.length === 0) return [];
        return [
            {
                header: t("AdvancePayment.ManagementPreviewSlicePeriodColumn"),
                align: "left",
                cellClassName: "slice-period-counted-cell",
                render: (row) => {
                    if (row.periodLinks.length === 0) return null;
                    const linkLabels = row.periodLinks.map((kind) => t(SLICE_PERIOD_LINK_LABEL_KEYS[kind]));
                    return (
                        <div className="slice-period-counted">
                            <CheckIcon className="slice-period-counted__check" aria-hidden />
                            <span>{linkLabels.join(" · ")}</span>
                        </div>
                    );
                },
            },
        ];
    }, [paymentSliceRows.length, t]);

    const handleViewInManagement = useCallback(() => {
        if (!tpd || !slice) return;
        const search = slice.legacyTermTitleEan13?.trim() || slice.title?.trim();
        if (search) {
            sessionStorage.setItem("management_initial_search", search);
        }
        onClose();
        navigate("/management", {
            state: {
                tpd: tpd.id,
                periodId: period?.id ?? periodId ?? undefined,
            },
        });
    }, [tpd, slice, period, periodId, onClose, navigate]);

    const isLoading = status === "pending";
    const showTable = status !== "failed";

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            style={MODAL_STYLE}
            mainTitle={t("AdvancePayment.ManagementPreviewModalTitlePrefix", { impactTarget: impactTargetLabel })}
        >
            <div className="management-preview-modal">
                {status === "failed" && (
                    <p className="management-preview-modal__error">{t("AdvancePayment.ManagementImpactModalError")}</p>
                )}

                {showTable && managementPreviewDisplay && (
                    <section className="management-preview-modal__section">
                        <TermManagementPreviewSection
                            display={managementPreviewDisplay}
                            period={period}
                            tpd={tpd}
                            isLoading={isLoading}
                            showViewInManagement
                            onViewInManagement={handleViewInManagement}
                        />
                    </section>
                )}

                {slice && (
                    <section className="management-preview-modal__section">
                        <Body style={SECTION_TITLE_STYLE}>{t("AdvancePayment.ManagementPreviewAdvanceSection")}</Body>
                        <AdvanceSlicesRecap<PaymentSlicePreviewRow>
                            slices={paymentSliceRows}
                            centerIndexColumn
                            trailingColumns={sliceImpactTrailingColumns}
                            getRowClassName={(row) => {
                                if (!hasImpactedSlices || !hasNonImpactedSlices) return undefined;
                                return row.periodLinks.length > 0
                                    ? "advance-slices-recap__row--impacted"
                                    : "advance-slices-recap__row--dimmed";
                            }}
                        />
                        <div className="management-preview-modal__advance-totals">
                            {hasImpactedSlices && (
                                <div className="management-preview-modal__advance-total">
                                    <SmallBody color="grey" tag="span">
                                        {t("AdvancePayment.ManagementPreviewAdvancePeriodTotal")}
                                    </SmallBody>
                                    <SmallBody tag="span" style={{ fontWeight: 500 }}>
                                        {formatNumber(impactedSlicesTotal, {
                                            isCurrency: true,
                                            always2Digit: true,
                                        })}
                                    </SmallBody>
                                </div>
                            )}
                            <div className="management-preview-modal__advance-total">
                                <SmallBody color="grey" tag="span">
                                    {t("AdvancePayment.ManagementPreviewAdvanceTotal")}
                                </SmallBody>
                                <SmallBody tag="span" style={{ fontWeight: 500 }}>
                                    {formatNumber(paymentSlicesTotal, {
                                        isCurrency: true,
                                        always2Digit: true,
                                    })}
                                </SmallBody>
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </ModalBox>
    );
};

export default ManagementPreviewModal;
